// src/components/web/landing/PricingSection.jsx

import React, { useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

/* ---------------------------------- */
/*          THEME & COLORS            */
/* ---------------------------------- */
const COLORS = {
  white: '#FFFFFF',
  black: '#212121',
  accentPink: '#F50057',  // Pink A400
  accentGreen: '#00E676', // Green A400
  accentRed: '#FF5252',   // Red A200
  accentGold: '#FFC107',  // Amber 500

  // Glassmorphic background
  glassBg: 'rgba(255, 255, 255, 0.1)',
  glassBorder: 'rgba(255, 255, 255, 0.3)',

  // For the heading/paragraph text
  textBright: '#F5F5F5',
};

/* ---------------------------------- */
/*          ANIMATIONS                */
/* ---------------------------------- */

/** Animate the gradient background. */
const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

/** Subtle arrow pulse for the back button. */
const pulseArrow = keyframes`
  0% {
    transform: translateX(0) scale(1);
  }
  50% {
    transform: translateX(-6px) scale(1.1);
  }
  100% {
    transform: translateX(0) scale(1);
  }
`;

/* ---------------------------------- */
/*         STYLED COMPONENTS          */
/* ---------------------------------- */

/**
 * PricingSectionContainer
 * 
 * A vibrant, animated gradient background with a "starry" overlay 
 * if you want to add extra effects. The grid glass panels are placed on top.
 */
const PricingSectionContainer = styled.section`
  position: relative;
  min-height: 100vh;
  width: 100%;
  padding: 3rem 1rem;
  color: ${COLORS.textBright};
  text-align: center;
  overflow: hidden;

  /* Animated gradient */
  background: linear-gradient(
    -45deg,
    #212121, /* Indigo 500 */
    #212121, /* Blue 500 */
    #212121, /* Cyan 500 */
    #212121  /* Green A400 */
  );
  background-size: 300% 300%;
  animation: ${gradientAnimation} 15s ease infinite;
`;

/**
 * Back Button
 * 
 * Floating at top-left, animates on hover for a "pulse" effect.
 */
const BackButton = styled.button`
  background: transparent;
  color: ${COLORS.white};
  font-size: 2rem;
  position: absolute;
  top: 2rem;
  left: 2rem;
  border: none;
  cursor: pointer;
  z-index: 10;
  transition: color 0.2s ease;
  &:hover {
    color: ${COLORS.accentPink};
    animation: ${pulseArrow} 0.6s ease;
  }
  &:focus {
    outline: 2px dashed ${COLORS.accentPink};
    outline-offset: 4px;
  }
`;

/**
 * Main Title & Description
 * 
 * Bold, modern typography with slight letter spacing.
 */
const SectionTitle = styled.h2`
  margin-top: 4rem;
  font-size: 2.5rem;
  font-weight: 800;
  letter-spacing: 1px;
  margin-bottom: 0.5rem;
`;

const SectionDescription = styled.p`
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0.5rem auto 3rem auto;
  line-height: 1.6;
  color: ${COLORS.white};
  opacity: 0.95;
`;

/**
 * PricingPlansGrid
 * 
 * A responsive grid for the plan cards.
 */
const PricingPlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

/**
 * PlanCard
 * 
 * Glassmorphic style:
 * - Transparent background
 * - Backdrop filter for blur
 * - Outer glow / highlight on hover
 */
const PlanCard = styled.div`
  position: relative;
  background: ${COLORS.glassBg};
  backdrop-filter: blur(10px);
  border-radius: 12px;
  border: 1px solid ${COLORS.glassBorder};
  padding: 2rem 1.5rem;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  text-align: left;

  ${({ isFree }) =>
    isFree &&
    css`
      /* "Free" corner ribbon */
      &::before {
        content: 'FREE';
        position: absolute;
        top: 0.8rem;
        right: -1.5rem;
        transform: rotate(45deg);
        background-color: ${COLORS.accentGold};
        color: ${COLORS.black};
        padding: 0.4rem 1rem;
        font-weight: 700;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
      }
    `}

  &:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.35);
  }

  .plan-name {
    font-size: 1.4rem;
    font-weight: 700;
    margin-bottom: 0.2rem;
    text-transform: uppercase;
    color: ${COLORS.accentGold};
    letter-spacing: 1px;
  }

  .plan-student-range {
    font-size: 0.9rem;
    font-style: italic;
    margin-bottom: 1rem;
    color: ${COLORS.textBright};
    opacity: 0.8;
  }

  .plan-price {
    font-size: 1.4rem;
    font-weight: bold;
    color: ${COLORS.white};
    margin-bottom: 1rem;
    ${({ isFree }) =>
      isFree &&
      css`
        color: ${COLORS.accentGold};
      `}
  }

  .plan-features {
    list-style: none;
    padding-left: 1rem;
    margin-bottom: 1.5rem;
    li {
      margin: 0.6rem 0;
      font-size: 0.95rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: ${COLORS.textBright};
      opacity: 0.9;
      &:last-child {
        font-weight: 600;
        color: ${COLORS.white};
        font-size: 1rem;
      }
    }
  }

  .plan-button {
    display: inline-block;
    background-color: ${({ isFree }) =>
      isFree ? COLORS.accentGold : COLORS.textBright};
    color: ${COLORS.black};
    border: none;
    border-radius: 6px;
    padding: 0.7rem 1.2rem;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    transition: all 0.3s ease;

    &:hover {
      color: ${COLORS.white};
      background-color: ${COLORS.accentPink};
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    }
    &:focus {
      outline: 2px solid ${COLORS.accentPink};
      outline-offset: 3px;
    }
    &:active {
      transform: translateY(1px);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    }
  }
`;

/* --------------------- MAIN COMPONENT --------------------- */

const PricingSection = () => {
  const navigate = useNavigate();

  // On mount, scroll to top
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Go back to previous page
  const handleBackClick = () => {
    navigate(-1);
  };

  const handlePlanButtonClick = (planName) => {
    navigate(`/school-owner/register?plan=${encodeURIComponent(planName)}`);
  };

  // Pricing data
  const pricingPlans = [
    {
      name: 'Free',
      studentRange: '(Up to 100 Students)',
      price: '₹0 /year',
      features: [
        { text: 'Mobile App Access', available: true },
        { text: 'Core school tools', available: true },
        { text: 'Up to 100 students', available: true },
        { text: 'Standard support', available: true },
        { text: 'No subscription fees', available: true },
        { text: 'Bus Tracking', available: false },
        { text: 'Free School Website', available: true },
      ],
      buttonText: 'Get Started Free',
    },
    {
      name: 'Standard',
      studentRange: '(Up to 500 Students)',
      price: '₹249/student/year',
      features: [
        { text: 'All Free features (Ad-Free)', available: true },
        { text: 'Advanced analytics', available: true },
        { text: 'Priority support', available: true },
        { text: 'Mobile App Access', available: true },
        { text: 'Bus Tracking', available: true },
        { text: 'Free School Website', available: true },
      ],
      buttonText: 'Choose Standard',
    },
    {
      name: 'Pro',
      studentRange: '(Up to 1000 Students)',
      price: '₹199/student/year',
      features: [
        { text: 'All Standard features', available: true },
        { text: 'Enhanced data insights', available: true },
        { text: 'Dedicated support', available: true },
        { text: 'Bus Tracking', available: true },
        { text: 'Free School Website', available: true },
      ],
      buttonText: 'Choose Pro',
    },
    {
      name: 'Enterprise',
      studentRange: '(1500+ Students)',
      price: '₹149/student/year',
      features: [
        { text: 'All Pro features', available: true },
        { text: 'Scalable 1500+ students', available: true },
        { text: 'Premium integrations', available: true },
        { text: 'Account manager', available: true },
        { text: 'Free School Website', available: true },
      ],
      buttonText: 'Choose Enterprise',
    },
  ];

  return (
    <PricingSectionContainer id="pricing-section">
      {/* Back Button */}
      <BackButton onClick={handleBackClick} aria-label="Go back to previous page">
        ←
      </BackButton>

      {/* Title & Description */}
      <SectionTitle>Pick Your Perfect Plan</SectionTitle>
      <SectionDescription>
        Explore flexible pricing that fits every school size and budget.
        Harness powerful administrative tools, all in one place.
      </SectionDescription>

      {/* Cards Grid */}
      <PricingPlansGrid>
        {pricingPlans.map((plan, index) => (
          <PlanCard key={index} isFree={plan.name === 'Free'}>
            <h3 className="plan-name">{plan.name}</h3>
            <div className="plan-student-range">{plan.studentRange}</div>
            <div className="plan-price">{plan.price}</div>

            <ul className="plan-features">
              {plan.features.map((feature, featureIndex) => (
                <li key={featureIndex}>
                  {feature.available ? (
                    <FaCheckCircle style={{ color: COLORS.accentGreen }} />
                  ) : (
                    <FaTimesCircle style={{ color: COLORS.accentRed }} />
                  )}
                  {feature.text}
                </li>
              ))}
            </ul>

            <button
              className="plan-button"
              onClick={() => handlePlanButtonClick(plan.name)}
            >
              {plan.buttonText}
            </button>
          </PlanCard>
        ))}
      </PricingPlansGrid>

    </PricingSectionContainer>
  );
};

export default PricingSection;
